import React, { useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Phone } from '@mui/icons-material';

import notifSystem from '../../notifSystem';
import { maskPhoneNumber } from '../../utils/phoneUtils';
import { RINGOVER_NEW_CLIENT_CALL_ENDED } from '../../utils/ringoverEvents';
import {
  startSocketListener,
  checkPhoneState,
  injectPhoneNumber,
  isCreationPage
} from './service';
/**
 * Composant de notification pour les appels de nouveaux clients.
 * Utilise newClientCallService pour communiquer avec les fiches.
 *
 * 2 comportements (jamais de pré-remplissage automatique):
 * 1. Fiche de création active dans l'onglet (vierge ou déjà remplie):
 *    popup avec bouton "Remplir le numéro" à cliquer explicitement.
 * 2. Fiche avec ID ou ailleurs: popup standard "Créer devis/intervention".
 *
 * Le popup reste affiché (pas d'auto-fermeture) jusqu'à ce que l'agent
 * clique une action, jusqu'à ce qu'il quitte la fiche (cas 1), ou jusqu'à
 * la fin de l'appel (event RINGOVER_NEW_CLIENT_CALL_ENDED, matché par
 * callId).
 */
const NewClientCallNotification = () => {
  const user = useSelector((state) => state.connectedUser);
  const location = useLocation();
  const navigate = useNavigate();
  const prevPathnameRef = useRef(location.pathname);

  const showStandardPopup = useCallback((phoneNumber, maskedPhone, callId) => {
    notifSystem.popUp(
      'Nouvel appel',
      maskedPhone,
      -1,
      {
        callId,
        icon: <Phone color="primary" />,
        actions: [
          {
            label: 'Créer un devis',
            color: 'primary',
            link: `/devis?phone=${phoneNumber}`,
            onClick: () => {
              navigate(`/devis?phone=${phoneNumber}`);
            },
            closeOnClick: true,
          },
          {
            label: 'Créer une intervention',
            color: 'primary',
            link: `/intervention?phone=${phoneNumber}`,
            onClick: () => {
              navigate(`/intervention?phone=${phoneNumber}`);
            },
            closeOnClick: true,
          },
        ],
      }
    );
  }, [navigate]);

  // Fiche de création active dans cet onglet (vierge ou déjà remplie)
  // -> l'agent doit valider manuellement, jamais de pré-remplissage.
  const showFillActionPopup = useCallback(
    (phoneNumber, maskedPhone, callId) => {
      notifSystem.popUp(
        'Nouvel appel',
        maskedPhone,
        -1,
        {
          callId,
          isFillAction: true,
          icon: <Phone color="primary" />,
          actions: [
            {
              label: 'Remplir le numéro',
              color: 'primary',
              onClick: () => {
                injectPhoneNumber(phoneNumber, true);
                notifSystem.success('Numéro rempli', maskedPhone);
              },
              closeOnClick: true,
            },
          ],
        }
      );
    },
    []
  );

  const handleNewClientCall = useCallback(
    (phoneNumber, callId) => {
      if (!phoneNumber) {
        return;
      }

      const maskedPhone = maskPhoneNumber(phoneNumber);
      const hasActiveFiche =
        isCreationPage(location.pathname) && checkPhoneState() !== null;

      if (hasActiveFiche) {
        showFillActionPopup(phoneNumber, maskedPhone, callId);
        return;
      }

      showStandardPopup(phoneNumber, maskedPhone, callId);
    },
    [location.pathname, showFillActionPopup, showStandardPopup]
  );

  useEffect(() => {
    if (!user?._id) {
      return undefined;
    }

    const cleanup = startSocketListener(handleNewClientCall);
    return cleanup;
  }, [user, handleNewClientCall]);

  // Ferme le(s) popup(s) "Remplir le numéro" si l'agent quitte la fiche
  // avant d'avoir cliqué.
  useEffect(() => {
    if (prevPathnameRef.current !== location.pathname) {
      notifSystem.closeMatching((notif) => notif.isFillAction);
      prevPathnameRef.current = location.pathname;
    }
  }, [location.pathname]);

  // Ferme le(s) popup(s) correspondant à l'appel terminé (raccroché), sauf
  // si l'agent a déjà cliqué une action (le popup n'existe alors plus).
  useEffect(() => {
    const handleCallEnded = (e) => {
      const { callId } = e.detail?.data || {};
      if (!callId) {
        return;
      }
      notifSystem.closeMatching((notif) => notif.callId === callId);
    };

    window.addEventListener(RINGOVER_NEW_CLIENT_CALL_ENDED, handleCallEnded);
    return () => window.removeEventListener(
      RINGOVER_NEW_CLIENT_CALL_ENDED, handleCallEnded
    );
  }, []);

  // Ce composant ne rend rien visuellement
  return null;
};

export default NewClientCallNotification;
